import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { usePageWrapper } from "../../../hooks";
import {
    APIService,
    ArtTable,
    ArtTableHandle,
    HeadCell,
    SSRTableBuilder,
    useApiService,
} from "@art/shared-ui";
import { useRef } from "react";
import { confirmDialog } from "@art/shared-ui";
import { ART_URL } from "../../../Constant";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { tableParamNav } from "../../../utils/table-utils";

interface Device {
    id: string;
    device_name: string;
    platform?: string;
    app_version?: string;
    last_ip?: string;
    is_active: boolean;
    is_current?: boolean;
    start_time: string;
    last_seen: string;
    end_time?: string | null;
}

const LinkedDevices = () => {
    const headCells: HeadCell<Device>[] = [
        {
            id: "device_name",
            numeric: false,
            disablePadding: false,
            label: "Device",
            getColData: (row) => (
                <Stack spacing={0.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={500}>
                            {row.device_name || "Unnamed device"}
                        </Typography>
                        {row.is_current && (
                            <Chip label="This device" size="small" color="primary" variant="outlined" />
                        )}
                    </Stack>
                    {(row.platform || row.app_version || row.last_ip) && (
                        <Typography variant="caption" color="text.secondary">
                            {[row.platform, row.app_version, row.last_ip].filter(Boolean).join(" · ")}
                        </Typography>
                    )}
                </Stack>
            ),
        },
        {
            id: "start_time",
            numeric: false,
            label: "First seen",
            disablePadding: false,
            getColData: (row) => (
                <Tooltip title={dayjs(row.start_time).format("MMMM D, YYYY h:mm A")}>
                    <span>{dayjs(row.start_time).format("MMM D, YYYY")}</span>
                </Tooltip>
            ),
        },
        {
            id: "last_seen",
            numeric: false,
            disablePadding: false,
            label: "Last active",
            getColData: (row) => (
                <Tooltip title={dayjs(row.last_seen).format("MMMM D, YYYY h:mm A")}>
                    <span>{relativeTime(row.last_seen)}</span>
                </Tooltip>
            ),
        },
        {
            id: "is_active",
            numeric: false,
            disablePadding: false,
            label: "Status",
            getColData: (row) =>
                row.is_active ? (
                    <Chip label="Active" color="success" size="small" />
                ) : (
                    <Chip label="Signed out" color="default" size="small" variant="outlined" />
                ),
        },
    ];

    const [queryParams, setQueryParams]: any = useSearchParams();
    const tableRef = useRef<ArtTableHandle>(null);
    const apiService = useApiService();
    const idpService = new APIService({
        ART_STORE: apiService.getStore(),
        BASE_URL: ART_URL + "/idp/api",
        DisableDefaultHeadder: true,
        DisableDefaultTenantUrl: true,
    });

    const tableBuilder = SSRTableBuilder<Device>({
        endpoint: "/linked-devices",
        method: "get",
        apiService: idpService,
        onFetch: (params) => {
            const currentPage = params["page"] || "1";
            setQueryParams({ page: currentPage }, tableParamNav(queryParams, "page", currentPage));
            // The IDP now uses ?page= (legacy was ?offset=, treated as page).
            return { page: currentPage };
        },
        onResponse: (data) => ({
            data: data?.data?.devices ?? [],
            total: data?.data?.total_count ?? 0,
        }),
    });

    const handleDelete = (row: Device) => {
        const isSelf = !!row.is_current;
        const title = isSelf ? "Sign out of this device?" : "Sign out this device?";
        const message = isSelf
            ? "You'll be signed out of this browser immediately. You can sign back in afterwards."
            : "Tokens issued to this device will stop working at the next refresh.";

        confirmDialog(title, message).then(() => {
            idpService.deleteData(`/linked-devices/${row.id}`, {
                onSuccess: () => {
                    if (isSelf) {
                        clearAuthCookiesAndReload();
                        return;
                    }
                    tableRef.current?.refresh();
                },
                onError: (err: any) => {
                    console.log(err);
                },
            })();
        });
    };

    return (
        <Box className="settings_table_page">
            <Box className="settings_table_header">
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    My Devices
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Browsers and apps signed in with your ART account appear here. Sign out a
                    device to revoke its access.
                </Typography>
            </Box>

            <Box className="settings_table_surface">
                <ArtTable
                    ref={tableRef}
                    headCells={headCells}
                    builder={tableBuilder}
                    search={{
                        enabled: false,
                        value: "",
                    }}
                    pagination={{
                        initialPage: parseInt(queryParams.get("page") || "1") - 1,
                    }}
                    rowActions={[
                        {
                            icon: faTrash,
                            label: "Sign out",
                            onClick: (row: Device) => handleDelete(row),
                        },
                    ]}
                />
            </Box>
        </Box>
    );
};

function relativeTime(ts: string): string {
    const t = new Date(ts).getTime();
    if (Number.isNaN(t)) return ts;
    const diff = Date.now() - t;
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} hr ago`;
    if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)} d ago`;
    return new Date(ts).toLocaleDateString();
}

function clearAuthCookiesAndReload() {
    ["ACCESS_TOKEN", "REFRESH_TOKEN", "TENANT_NAME"].forEach((name) => {
        document.cookie = `${name}=; Max-Age=0; Path=/`;
    });
    const finalUrl = `${ART_URL}/`;
    window.location.assign(
        `${ART_URL}/idp/auth/tenant/logout?redirect_uri=${encodeURIComponent(finalUrl)}`
    );
}

export default usePageWrapper(LinkedDevices, {
    page_title: "My Devices",
});
